/**
 * AttivitaFormModal — crea / modifica una singola attività del Gantt.
 * Estratto da CantiereGanttView.jsx.
 * Include: date, orari, persone assegnate con rilevazione conflitti,
 * nota importante urgente, sync automatico calendario.
 */
import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import { GanttInput as Input } from "./ganttConstants";

export default function AttivitaFormModal({ cantiereId, cronoId, attivita, onClose, qc }) {
  const isEdit = !!attivita;
  const [form, setForm] = React.useState({
    nome: attivita?.nome || "",
    categoria: attivita?.categoria || "",
    data_inizio: attivita?.data_inizio || new Date().toISOString().slice(0, 10),
    data_fine: attivita?.data_fine || new Date().toISOString().slice(0, 10),
    ora_inizio: attivita?.ora_inizio || "",
    ora_fine: attivita?.ora_fine || "",
    data_inizio_effettiva: attivita?.data_inizio_effettiva || "",
    data_fine_effettiva: attivita?.data_fine_effettiva || "",
    responsabile: attivita?.responsabile || "",
    squadra_fornitore: attivita?.squadra_fornitore || "",
    importo: attivita?.importo ?? 0,
    data_fatturazione_prevista: attivita?.data_fatturazione_prevista || "",
    stato: attivita?.stato || "programmata",
    percentuale_avanzamento: attivita?.percentuale_avanzamento || 0,
    ore_previste: attivita?.ore_previste || 0,
    note: attivita?.note || "",
    nota_importante: attivita?.nota_importante || "",
    nota_importante_rossa: attivita?.nota_importante_rossa || false,
    attesa_materiale: attivita?.attesa_materiale || { materiale_atteso: "", fornitore: "", data_ordine: "", data_prevista_consegna: "", numero_ordine: "", note: "" },
    lavoratori_assegnati: attivita?.lavoratori_assegnati || [],
    add_to_calendar: attivita?.evento_id ? true : false,
  });

  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori-attivi"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });
  const { data: allAtt = [] } = useQuery({
    queryKey: ["all-attivita-per-conflitti"],
    queryFn: () => api.get(`/cronoprogramma?cantiere_id=${cantiereId}`).then((r) => r.data),
    enabled: !!cantiereId,
  });

  const [personSearch, setPersonSearch] = React.useState("");
  const filteredLav = React.useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return lavoratori;
    return lavoratori.filter((l) => (l.nome_completo || l.nome || "").toLowerCase().includes(q));
  }, [lavoratori, personSearch]);

  const conflitti = React.useMemo(() => {
    if (!form.lavoratori_assegnati.length || !form.data_inizio || !form.data_fine) return [];
    const di = form.data_inizio, df = form.data_fine;
    const overlaps = (a) => {
      if (attivita && a.attivita_id === attivita.attivita_id) return false;
      return !(a.data_fine < di || a.data_inizio > df);
    };
    const out = [];
    for (const lid of form.lavoratori_assegnati) {
      const nome = (lavoratori.find((l) => l.lavoratore_id === lid) || {}).nome_completo || lid;
      const conflictAtt = allAtt.filter((a) => (a.lavoratori_assegnati || []).includes(lid) && overlaps(a));
      if (conflictAtt.length > 0) {
        out.push({ lavoratore: nome, attivita: conflictAtt.map((a) => `${a.nome} (${a.data_inizio}→${a.data_fine})`) });
      }
    }
    return out;
  }, [form.lavoratori_assegnati, form.data_inizio, form.data_fine, allAtt, lavoratori, attivita]);

  const toggleLav = (lid) => {
    setForm((f) => ({
      ...f,
      lavoratori_assegnati: f.lavoratori_assegnati.includes(lid)
        ? f.lavoratori_assegnati.filter((x) => x !== lid)
        : [...f.lavoratori_assegnati, lid],
    }));
  };

  const save = useMutation({
    mutationFn: (p) => {
      if (isEdit) return api.patch(`/cronoprogramma/${attivita.attivita_id}`, p).then((r) => r.data);
      return api.post("/cronoprogramma", { ...p, cantiere_id: cantiereId, cronoprogramma_id: cronoId }).then((r) => r.data);
    },
    onSuccess: (res) => {
      const nNotif = res?._notifiche_inviate;
      const baseMsg = isEdit ? "Attività aggiornata — calendario e assegnazioni sincronizzati" : "Attività creata";
      const notifMsg = typeof nNotif === "number" && nNotif > 0
        ? ` · 📱 ${nNotif} notifica${nNotif === 1 ? "" : "e"} inviate ai lavoratori`
        : "";
      toast.success(baseMsg + notifMsg);
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantiereId] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["eventi"] });
      qc.invalidateQueries({ queryKey: ["calendario"] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const isAttesa = form.stato === "attesa_materiale";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="attivita-modal">
        <div className="p-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold">{isEdit ? "Modifica attività" : "Nuova attività"}</h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Input label="Nome *" testid="input-att-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Categoria" value={form.categoria} onChange={(v) => setForm({ ...form, categoria: v })} />
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Stato</label>
              <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="programmata">Programmata (giallo)</option>
                <option value="in_corso">In corso (arancione)</option>
                <option value="eseguita">Eseguita (verde)</option>
                <option value="consegnato">Consegnato (blu)</option>
                <option value="attesa_materiale">Attesa materiale (grigio)</option>
              </select>
            </div>
            <Input label="Data inizio prevista *" type="date" value={form.data_inizio} onChange={(v) => setForm({ ...form, data_inizio: v })} />
            <Input label="Data fine prevista *" type="date" value={form.data_fine} onChange={(v) => setForm({ ...form, data_fine: v })} />
            <Input label="Inizio effettivo" type="date" value={form.data_inizio_effettiva} onChange={(v) => setForm({ ...form, data_inizio_effettiva: v })} />
            <Input label="Fine effettivo" type="date" value={form.data_fine_effettiva} onChange={(v) => setForm({ ...form, data_fine_effettiva: v })} />
            <Input label="Responsabile" value={form.responsabile} onChange={(v) => setForm({ ...form, responsabile: v })} />
            <Input label="Squadra / Fornitore" value={form.squadra_fornitore} onChange={(v) => setForm({ ...form, squadra_fornitore: v })} />
            <Input label="Importo (€)" type="number" value={form.importo} onChange={(v) => setForm({ ...form, importo: parseFloat(v || 0) })} />
            <Input label="Data fatturazione prevista" type="date" value={form.data_fatturazione_prevista} onChange={(v) => setForm({ ...form, data_fatturazione_prevista: v })} />
            <Input label="Ore previste" type="number" value={form.ore_previste} onChange={(v) => setForm({ ...form, ore_previste: parseFloat(v || 0) })} />
            <Input label="% completamento" type="number" value={form.percentuale_avanzamento} onChange={(v) => setForm({ ...form, percentuale_avanzamento: parseFloat(v || 0) })} />
          </div>

          {isAttesa && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-md space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Attesa materiale</div>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Materiale atteso" value={form.attesa_materiale.materiale_atteso} onChange={(v) => setForm({ ...form, attesa_materiale: { ...form.attesa_materiale, materiale_atteso: v } })} />
                <Input label="Fornitore" value={form.attesa_materiale.fornitore} onChange={(v) => setForm({ ...form, attesa_materiale: { ...form.attesa_materiale, fornitore: v } })} />
                <Input label="Data ordine" type="date" value={form.attesa_materiale.data_ordine || ""} onChange={(v) => setForm({ ...form, attesa_materiale: { ...form.attesa_materiale, data_ordine: v } })} />
                <Input label="Data prevista consegna" type="date" value={form.attesa_materiale.data_prevista_consegna || ""} onChange={(v) => setForm({ ...form, attesa_materiale: { ...form.attesa_materiale, data_prevista_consegna: v } })} />
                <Input label="N. ordine" value={form.attesa_materiale.numero_ordine} onChange={(v) => setForm({ ...form, attesa_materiale: { ...form.attesa_materiale, numero_ordine: v } })} />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
            <textarea rows="2" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>

          {/* === NOTA IMPORTANTE === */}
          <div className={`border rounded-md p-3 space-y-2 ${form.nota_importante_rossa ? "border-rose-400 bg-rose-50" : "border-amber-300 bg-amber-50/40"}`}>
            <div className={`text-xs font-bold uppercase tracking-widest ${form.nota_importante_rossa ? "text-rose-700" : "text-amber-800"} flex items-center gap-1`}>
              {form.nota_importante_rossa && <span>⚠️</span>}
              Nota importante (facoltativa)
            </div>
            <textarea
              rows="2"
              value={form.nota_importante}
              onChange={(e) => setForm({ ...form, nota_importante: e.target.value })}
              placeholder="Es. ATTENDERE ARRIVO MATERIALE · ACCESSO AL CANTIERE SOLO DALLE 8:00 · CONTATTARE CLIENTE PRIMA"
              data-testid="input-nota-importante"
              className={`w-full px-3 py-2 border rounded-sm text-sm font-semibold ${form.nota_importante_rossa ? "border-rose-400 text-rose-900 bg-white" : "border-amber-300 bg-white"}`}
            />
            <label className="flex items-center gap-2 cursor-pointer" data-testid="input-nota-rossa">
              <input
                type="checkbox"
                checked={!!form.nota_importante_rossa}
                onChange={(e) => setForm({ ...form, nota_importante_rossa: e.target.checked })}
                className="w-4 h-4 accent-rose-600"
              />
              <span className={`text-xs font-bold ${form.nota_importante_rossa ? "text-rose-700" : "text-slate-700"}`}>
                ⚠️ Evidenzia in ROSSO — apparirà con badge <span className="inline-block px-1.5 py-0.5 bg-rose-600 text-white rounded-sm text-[10px] font-bold">⚠️ !</span> in Cronoprogramma e Calendario
              </span>
            </label>
            {form.nota_importante && (
              <div className={`p-2 rounded-sm border text-xs ${form.nota_importante_rossa ? "bg-rose-100 border-rose-300 text-rose-900 font-bold" : "bg-white border-amber-200 text-amber-900"}`}>
                <div className="text-[10px] uppercase font-bold opacity-70 mb-1">Anteprima</div>
                {form.nota_importante_rossa && "⚠️ "}{form.nota_importante}
              </div>
            )}
          </div>

          {/* === PIANIFICAZIONE === */}
          <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3 space-y-2">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-800">📅 Pianificazione attività</div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Ora inizio (facoltativa)" type="time" value={form.ora_inizio} onChange={(v) => setForm({ ...form, ora_inizio: v })} testid="input-att-ora-inizio" />
              <Input label="Ora fine (facoltativa)" type="time" value={form.ora_fine} onChange={(v) => setForm({ ...form, ora_fine: v })} testid="input-att-ora-fine" />
            </div>
            <label
              className="flex items-center gap-2 p-2 border border-blue-300 bg-white rounded-sm cursor-pointer"
              data-testid="attivita-add-to-calendar"
            >
              <input
                type="checkbox"
                checked={!!form.add_to_calendar}
                onChange={(e) => setForm({ ...form, add_to_calendar: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-xs text-blue-900">
                <b>{attivita?.evento_id ? "Evento calendario collegato" : "Aggiungi al Calendario"}</b>
                {" — "}{form.data_inizio}{form.ora_inizio && ` ${form.ora_inizio}`} → {form.data_fine}{form.ora_fine && ` ${form.ora_fine}`}
              </span>
            </label>
          </div>

          {/* === PERSONE === */}
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3 space-y-2">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-800 flex items-center justify-between">
              <span>👥 Persone assegnate ({form.lavoratori_assegnati.length})</span>
              {form.lavoratori_assegnati.length > 0 && (
                <button type="button" onClick={() => setForm({ ...form, lavoratori_assegnati: [] })}
                  className="text-[10px] font-semibold text-rose-700 hover:underline">svuota</button>
              )}
            </div>
            <input
              type="text"
              placeholder="🔎 Cerca persona per nome…"
              value={personSearch}
              onChange={(e) => setPersonSearch(e.target.value)}
              data-testid="input-persone-search"
              data-no-uppercase
              className="w-full px-3 py-2 border border-emerald-300 rounded-sm text-sm bg-white"
            />
            <div className="max-h-40 overflow-y-auto border border-emerald-200 rounded-sm bg-white">
              {filteredLav.length === 0 ? (
                <div className="p-3 text-xs text-slate-400 italic text-center">Nessuna persona</div>
              ) : (
                filteredLav.map((l) => {
                  const checked = form.lavoratori_assegnati.includes(l.lavoratore_id);
                  return (
                    <label key={l.lavoratore_id}
                      className={`flex items-center gap-2 p-2 border-b border-slate-100 cursor-pointer hover:bg-emerald-50 ${checked ? "bg-emerald-100" : ""}`}
                      data-testid={`persona-${l.lavoratore_id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleLav(l.lavoratore_id)} className="w-4 h-4" />
                      <span className="text-xs font-semibold">{l.nome_completo || l.nome}</span>
                      {l.ruolo && <span className="ml-auto text-[10px] text-slate-500 uppercase">{l.ruolo}</span>}
                    </label>
                  );
                })
              )}
            </div>
            {conflitti.length > 0 && (
              <div className="p-2 bg-amber-100 border border-amber-400 rounded-sm text-xs text-amber-900" data-testid="conflitto-warning">
                <div className="font-bold flex items-center gap-1">⚠️ Conflitto di sovrapposizione</div>
                {conflitti.map((c, i) => (
                  <div key={i} className="mt-1">
                    <b>{c.lavoratore}</b> è già assegnato a: {c.attivita.join(", ")}
                  </div>
                ))}
                <div className="mt-1 italic text-[10px]">Il salvataggio è comunque consentito.</div>
              </div>
            )}
            {form.lavoratori_assegnati.length > 0 && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-sm text-[11px] text-blue-900" data-testid="notifica-info">
                📱 Al salvataggio verranno inviate notifiche push (o email di fallback) ai lavoratori appena assegnati.
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
          <button onClick={() => save.mutate(form)} disabled={save.isPending || !form.nome} data-testid="btn-save-att" className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm">{save.isPending ? "…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}
